import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import dayjs from 'dayjs';

import { ImportJobStatus, ImportStatus } from '../../types/Box';
import { ApiError } from '../../types/Error';
import { fetchBoxStatus, fetchScanStatus } from '../Scans';
import { getBoxQueryKey } from './useGetBox';
import { getScanQueryKey } from './useGetScan';

/**
 * useGetImportStatus is a polling hook that tracks the status of an import job.
 *
 * Polling begins when any of the following are true:
 * - `shouldStartOnMount` (e.g., on component mount),
 * - `isInternallyEnabled` (e.g., triggered internally), or
 * - `hasManualTrigger` (triggered by user action), and the job status is still in progress (not `COMPLETED` or `FAILED`).
 *
 * - `setImportJobStatus` updates the latest import job status.
 * - `onTimeout` is called if polling exceeds a defined timeout duration.
 */

interface Props {
    scanId: string | number | undefined;
    boxId: string | number | undefined;
    shouldStartOnMount: boolean;
    onError: (err: unknown) => void;
    hasManualTrigger?: boolean;
    onTimeout?: () => void;
    onStatusComplete?: () => void;
}

const TIMEOUT_LIMIT_S = 40; // 40 seconds
const POLLING_SCAN_ITEM_STATUS_MS = 8_000; // 8 seconds

export const useGetImportStatus = ({
    scanId,
    boxId,
    shouldStartOnMount,
    hasManualTrigger = false,
    onError,
    onTimeout,
    onStatusComplete
}: Props): { importJobStatus: ImportJobStatus | undefined; error: ApiError | null } => {
    const queryClient = useQueryClient();
    const pendingSinceRef = useRef<number | null>(null);
    const [importJobStatus, setImportJobStatus] = useState<ImportJobStatus>();

    const isJobOngoing = importJobStatus === 'PENDING' || importJobStatus === 'PROCESSING';
    const isPollingEnabled = shouldStartOnMount && (isJobOngoing || hasManualTrigger);

    const query = useQuery<ImportStatus | undefined, ApiError>({
        queryKey: ['import-status', scanId ?? null, boxId ?? null],
        queryFn: async () => {
            if (!scanId) return Promise.resolve(undefined);
            return boxId ? fetchBoxStatus({ scanId, boxId }) : fetchScanStatus({ scanId });
        },
        enabled: shouldStartOnMount,
        staleTime: 0, // Always refetch
        retry: false,
        refetchInterval: isPollingEnabled ? POLLING_SCAN_ITEM_STATUS_MS : undefined
    });

    useEffect(() => {
        const jobStatus = query.data?.jobStatus;

        if (jobStatus === 'PENDING') {
            if (!pendingSinceRef.current) {
                pendingSinceRef.current = dayjs().valueOf();
            } else {
                const secondsPending = dayjs().diff(pendingSinceRef.current, 'seconds');

                if (secondsPending >= TIMEOUT_LIMIT_S) {
                    setImportJobStatus(undefined);
                    pendingSinceRef.current = null;
                    onTimeout?.();
                }
            }
        } else {
            pendingSinceRef.current = null; // Reset timer if status is not pending
        }

        if (jobStatus === 'COMPLETED') {
            // Invalidate queries to refresh the UI (Detail view and Scanning Overview) with the latest data.
            // After invalidation, mark the import job as COMPLETED.
            Promise.all([
                queryClient.invalidateQueries({ queryKey: getBoxQueryKey(boxId) }),
                queryClient.invalidateQueries({ queryKey: getScanQueryKey(String(scanId)) }),
                queryClient.invalidateQueries({ queryKey: ['scans'] })
            ]).finally(() => {
                setImportJobStatus(undefined);
                onStatusComplete?.();
            });
        } else {
            setImportJobStatus(jobStatus);
        }
    }, [query.data, query.isFetching]); // We need is fetching here to ensure the effect runs when the query is refetched

    useEffect(() => {
        if (query.error) {
            // We set importJobStatus as undefined to indicate that the job is fulfilled and initialize the variable
            setImportJobStatus(undefined);

            // We ignore a 404 error, as by default scan/boxes do not have an import job.
            if (shouldIgnoreMissingScanJob(query.error)) {
                return; // Silently ignore 404 error
            }
            onError(query.error);
        }
    }, [query.error]);

    return { importJobStatus, error: query.error };
};

// TODO: SS-510 - Remove this function once we have the logic in the back-end.
//  Currently, there is no way for the client to know if a scan/box has an import job or not.
//  So we will have to check this for every scan/box that is fetched.
//  Possible fix would be to add a importStatus property on the scan/box object.
export const shouldIgnoreMissingScanJob = (error: unknown): boolean => {
    return isAxiosError(error) && error.response?.status === 404;
};
